<section class="sav-category-showcase-page" aria-label="Premium Category Showcase">
  <div class="sav-showcase-shell">
    <app-breadcrumb [items]="crumbs" />

    <section class="sav-featured-section" aria-label="Featured categories">
      <header class="sav-section-head with-actions">
        <div class="sav-section-title-wrap">
          <span class="badge">Editor's Choice</span>
          @if (pageH1()) {
            <h1>{{ pageH1() }}</h1>
          } @else {
            <h1>Featured <span>Categories</span></h1>
          }
          <p>
            {{ visibleShortDescription }}
            @if (shouldShowShortDescriptionToggle) {
              <button type="button" class="sav-read-more-inline" (click)="toggleShortDescription()">
                {{ isShortDescriptionExpanded ? 'Read Less' : 'Read More' }}
              </button>
            }
          </p>
        </div>

        @if (shouldShowFeaturedControls()) {
          <div class="sav-head-actions">
            <button
              type="button"
              class="sav-action-btn sav-action-btn-ghost"
              aria-label="Previous categories"
              (click)="slideFeaturedCategories('prev')"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M14.5 5.25 7.75 12l6.75 6.75"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              class="sav-action-btn sav-action-btn-solid"
              aria-label="Next categories"
              (click)="slideFeaturedCategories('next')"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M9.5 5.25 16.25 12 9.5 18.75"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        }
      </header>

      @if (isLoadingCategories()) {
        <div
          class="sav-featured-grid sav-featured-skeleton-grid"
          aria-label="Loading featured categories"
        >
          @for (item of [1, 2, 3, 4]; track item) {
            <div class="sav-featured-card sav-category-skeleton-card">
              <span
                class="sav-featured-icon sav-category-skeleton sav-category-skeleton-icon"
                aria-hidden="true"
              ></span>
              <div class="sav-featured-card-content sav-category-skeleton-content">
                <span class="sav-category-skeleton sav-category-skeleton-title"></span>
                <span class="sav-category-skeleton sav-category-skeleton-meta"></span>
              </div>
            </div>
          }
        </div>
      } @else if (featuredCategories().length > 0) {
        <div #featuredCarousel class="sav-featured-grid sav-featured-carousel">
          @for (category of featuredCategories(); track category.slug) {
            <div class="sav-featured-slide">
              <a class="sav-featured-card" [href]="'/categories/' + category.slug">
                <span class="sav-featured-icon" aria-hidden="true">
                  @if (category.iconHtml) {
                    <span class="sav-api-category-icon-code" [innerHTML]="category.iconHtml"></span>
                  } @else if (category.iconImageUrl) {
                    <img
                      class="sav-api-category-icon-image"
                      [src]="category.iconImageUrl"
                      [alt]="category.title"
                      (error)="showFallbackIcon($event)"
                    />
                    <svg
                      class="sav-api-category-fallback-svg"
                      viewBox="0 0 32 32"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M5.5 26V8.5L16 4l10.5 4.5V26"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M12 26v-7h8v7M10.5 13h3M10.5 17h3M18.5 13h3M18.5 17h3"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                      />
                    </svg>
                  } @else {
                    @switch (category.icon) {
                      @case ('apparel') {
                        <svg viewBox="0 0 32 32" fill="none">
                          <path
                            d="m5 11 4.8-5h3.6l2.6 3h0l2.6-3h3.6l4.8 5v4.2l-3.8 1.9L21 13.6V26H11V13.6L8.8 17.1 5 15.2V11Z"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linejoin="round"
                          />
                        </svg>
                      }
                      @case ('ai') {
                        <svg viewBox="0 0 32 32" fill="none">
                          <rect
                            x="7"
                            y="8"
                            width="18"
                            height="16"
                            rx="4"
                            stroke="currentColor"
                            stroke-width="2"
                          />
                          <circle cx="13" cy="16" r="1.7" fill="currentColor" />
                          <circle cx="19" cy="16" r="1.7" fill="currentColor" />
                          <path
                            d="M16 4.5V8M9.5 5.8l1.8 2.7M22.5 5.8l-1.8 2.7M4.5 16H7M25 16h2.5M10.5 22.5c1.4 1.5 3.1 2.3 5.5 2.3 2.4 0 4.1-.8 5.5-2.3"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                          />
                        </svg>
                      }
                      @case ('electronics') {
                        <svg viewBox="0 0 32 32" fill="none">
                          <rect
                            x="9"
                            y="4"
                            width="14"
                            height="24"
                            rx="3"
                            stroke="currentColor"
                            stroke-width="2"
                          />
                          <path
                            d="M13 7.5h6"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                          />
                          <circle cx="16" cy="24" r="1.6" fill="currentColor" />
                        </svg>
                      }
                      @case ('entertainment') {
                        <svg viewBox="0 0 32 32" fill="none">
                          <path
                            d="M7 10.5c3.8-2.2 7.2-2.2 10.2 0 3-2.2 6.4-2.2 10.2 0l-1.5 11.2c-.3 2.3-2.2 4-4.5 4-2 0-3.1-1-4.2-2.5-1.1 1.5-2.2 2.5-4.2 2.5-2.3 0-4.2-1.7-4.5-4L7 10.5Z"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M11.8 15.5h3M21.8 15.5h3M13.2 20.2c1.3 1.2 2.9 1.2 4.2 0M20.8 20.2c1.3 1.2 2.9 1.2 4.2 0"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                          />
                        </svg>
                      }
                      @default {
                        <svg viewBox="0 0 32 32" fill="none">
                          <path
                            d="M5.5 26V8.5L16 4l10.5 4.5V26"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M12 26v-7h8v7M10.5 13h3M10.5 17h3M18.5 13h3M18.5 17h3"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                          />
                        </svg>
                      }
                    }
                  }
                </span>
                <div class="sav-featured-card-content">
                  <h2 class="type-card-title">{{ category.title }}</h2>
                  <div class="sav-featured-pills">
                    <span class="sav-featured-pill">{{ category.coupons }} coupons</span>
                    <span class="sav-featured-pill">{{ category.stores }} stores</span>
                  </div>
                </div>
              </a>
            </div>
          }
        </div>
      } @else {
        <div class="sav-featured-empty-state" role="status">
          <p>
            {{
              hasCategoryError()
                ? 'Categories could not be loaded right now.'
                : 'No featured categories are available right now.'
            }}
          </p>
        </div>
      }
    </section>

    <section id="all-categories" class="sav-all-categories" aria-label="All categories">
      <header class="sav-section-head">
        <div class="sav-section-title-wrap">
          <h2>Explore All Categories</h2>
          <p>Browse our comprehensive selection of curated categories across all sectors.</p>
        </div>
      </header>

      <div class="sav-all-groups">
        @if (isLoadingCategories()) {
          @for (item of [1, 2, 3, 4, 5, 6]; track item) {
            <article class="sav-group-card sav-group-card-skeleton">
              <header>
                <span
                  class="sav-group-icon sav-category-skeleton sav-category-skeleton-icon"
                  aria-hidden="true"
                ></span>
                <span class="sav-category-skeleton sav-category-skeleton-group-title"></span>
              </header>
              <div class="sav-group-items">
                @for (child of [1, 2, 3, 4]; track child) {
                  <span class="sav-category-skeleton-row">
                    <span
                      class="sav-subcategory-icon sav-category-skeleton sav-category-skeleton-sub-icon"
                      aria-hidden="true"
                    ></span>
                    <span class="sav-category-skeleton sav-category-skeleton-item"></span>
                  </span>
                }
              </div>
            </article>
          }
        } @else {
          @for (group of categoryGroups(); track group.slug) {
            <article class="sav-group-card">
              <header>
                <span class="sav-group-icon" aria-hidden="true">
                  @if (group.iconHtml) {
                    <span class="sav-api-category-icon-code" [innerHTML]="group.iconHtml"></span>
                  } @else if (group.iconImageUrl) {
                    <img
                      class="sav-api-category-icon-image"
                      [src]="group.iconImageUrl"
                      [alt]="group.title"
                      (error)="showFallbackIcon($event)"
                    />
                    <svg
                      class="sav-api-category-fallback-svg"
                      viewBox="0 0 32 32"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M5.5 26V8.5L16 4l10.5 4.5V26"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M12 26v-7h8v7M10.5 13h3M10.5 17h3M18.5 13h3M18.5 17h3"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                      />
                    </svg>
                  } @else {
                    @switch (group.icon) {
                      @case ('apparel') {
                        <svg viewBox="0 0 32 32" fill="none">
                          <path
                            d="m5 11 4.8-5h3.6l2.6 3h0l2.6-3h3.6l4.8 5v4.2l-3.8 1.9L21 13.6V26H11V13.6L8.8 17.1 5 15.2V11Z"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linejoin="round"
                          />
                        </svg>
                      }
                      @case ('ai') {
                        <svg viewBox="0 0 32 32" fill="none">
                          <rect
                            x="7"
                            y="8"
                            width="18"
                            height="16"
                            rx="4"
                            stroke="currentColor"
                            stroke-width="2"
                          />
                          <circle cx="13" cy="16" r="1.7" fill="currentColor" />
                          <circle cx="19" cy="16" r="1.7" fill="currentColor" />
                          <path
                            d="M16 4.5V8M9.5 5.8l1.8 2.7M22.5 5.8l-1.8 2.7M4.5 16H7M25 16h2.5M10.5 22.5c1.4 1.5 3.1 2.3 5.5 2.3 2.4 0 4.1-.8 5.5-2.3"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                          />
                        </svg>
                      }
                      @case ('electronics') {
                        <svg viewBox="0 0 32 32" fill="none">
                          <rect
                            x="9"
                            y="4"
                            width="14"
                            height="24"
                            rx="3"
                            stroke="currentColor"
                            stroke-width="2"
                          />
                          <path
                            d="M13 7.5h6"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                          />
                          <circle cx="16" cy="24" r="1.6" fill="currentColor" />
                        </svg>
                      }
                      @case ('entertainment') {
                        <svg viewBox="0 0 32 32" fill="none">
                          <path
                            d="M7 10.5c3.8-2.2 7.2-2.2 10.2 0 3-2.2 6.4-2.2 10.2 0l-1.5 11.2c-.3 2.3-2.2 4-4.5 4-2 0-3.1-1-4.2-2.5-1.1 1.5-2.2 2.5-4.2 2.5-2.3 0-4.2-1.7-4.5-4L7 10.5Z"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M11.8 15.5h3M21.8 15.5h3M13.2 20.2c1.3 1.2 2.9 1.2 4.2 0M20.8 20.2c1.3 1.2 2.9 1.2 4.2 0"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                          />
                        </svg>
                      }
                      @default {
                        <svg viewBox="0 0 32 32" fill="none">
                          <path
                            d="M5.5 26V8.5L16 4l10.5 4.5V26"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M12 26v-7h8v7M10.5 13h3M10.5 17h3M18.5 13h3M18.5 17h3"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                          />
                        </svg>
                      }
                    }
                  }
                </span>
                <!-- Every top-level category carries the title type, whether or not it has children.
                     A childless one used to drop to the surrounding text style, which made it
                     indistinguishable from a sub-item of the group above it — "Cars" read as if it
                     sat inside "Business & Finance". Only the rule and the uppercase treatment are
                     still reserved for groups that actually head a list (see the stylesheet), so
                     this does not turn the directory back into a wall of headings. -->
                <h2 class="type-card-title">
                  <a [href]="'/categories/' + group.slug">{{ group.title }}</a>
                </h2>
              </header>
              @if (group.items.length > 0) {
                <div class="sav-group-items">
                  @for (item of group.items; track item.label) {
                    <a [href]="'/categories/' + item.slug">
                      <span class="sav-subcategory-icon" aria-hidden="true">
                        @if (item.iconHtml) {
                          <span
                            class="sav-api-category-icon-code"
                            [innerHTML]="item.iconHtml"
                          ></span>
                        } @else if (item.iconImageUrl) {
                          <img
                            class="sav-api-category-icon-image"
                            [src]="item.iconImageUrl"
                            [alt]="item.label"
                            (error)="showFallbackIcon($event)"
                          />
                          <svg
                            class="sav-api-category-fallback-svg"
                            viewBox="0 0 32 32"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M5.5 26V8.5L16 4l10.5 4.5V26"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M12 26v-7h8v7M10.5 13h3M10.5 17h3M18.5 13h3M18.5 17h3"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                            />
                          </svg>
                        } @else {
                          @switch (item.icon) {
                            @case ('shirt') {
                              <svg viewBox="0 0 20 20" fill="none">
                                <path
                                  d="M3.2 6.5 5.8 4h2l1.4 1.7h1.6L12.2 4h2l2.6 2.5v2.2l-2 .9-1.3-2.1V16H6.5V7.5L5.2 9.6l-2-.9V6.5Z"
                                  stroke="currentColor"
                                  stroke-width="1.4"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            }
                            @case ('device') {
                              <svg viewBox="0 0 20 20" fill="none">
                                <rect
                                  x="6"
                                  y="2.5"
                                  width="8"
                                  height="15"
                                  rx="2"
                                  stroke="currentColor"
                                  stroke-width="1.4"
                                />
                                <path
                                  d="M8.3 5h3.4"
                                  stroke="currentColor"
                                  stroke-width="1.4"
                                  stroke-linecap="round"
                                />
                                <circle cx="10" cy="14.7" r=".8" fill="currentColor" />
                              </svg>
                            }
                            @case ('briefcase') {
                              <svg viewBox="0 0 20 20" fill="none">
                                <rect
                                  x="2.5"
                                  y="6"
                                  width="15"
                                  height="10"
                                  rx="2"
                                  stroke="currentColor"
                                  stroke-width="1.4"
                                />
                                <path
                                  d="M7 6V4.8A1.8 1.8 0 0 1 8.8 3h2.4A1.8 1.8 0 0 1 13 4.8V6M2.5 10h15"
                                  stroke="currentColor"
                                  stroke-width="1.4"
                                  stroke-linecap="round"
                                />
                              </svg>
                            }
                            @default {
                              <svg viewBox="0 0 20 20" fill="none">
                                <path
                                  d="M10 2.8 11.7 7l4.5.4-3.4 3 1 4.5-3.8-2.3L6.2 15l1-4.5-3.4-3L8.3 7 10 2.8Z"
                                  stroke="currentColor"
                                  stroke-width="1.4"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            }
                          }
                        }
                      </span>
                      <span>{{ item.label }}</span>
                    </a>
                  }
                </div>
              }
            </article>
          } @empty {
            <div class="sav-featured-empty-state" role="status">
              <p>
                {{
                  hasCategoryError()
                    ? 'Categories could not be loaded right now.'
                    : 'No categories are available right now.'
                }}
              </p>
            </div>
          }
        }
      </div>
    </section>

    @defer (on idle) {
      <section class="sav-faq-section" aria-label="Frequently Asked Questions">
        <div class="sav-faq-wrap">
          <h2>Frequently Asked Questions</h2>
          <p>Common questions about coupon categories and how to use them.</p>
          <div class="sav-faq-list">
            @for (faq of displayedFaqs(); track $index) {
              <div class="sav-faq-item" [class.expanded]="expandedFaqIndex() === $index">
                <button
                  class="sav-faq-question"
                  type="button"
                  [attr.aria-expanded]="expandedFaqIndex() === $index"
                  (click)="toggleFaq($index)"
                >
                  <span>{{ faq.question }}</span>
                  <svg
                    class="sav-faq-chevron"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
                <div class="sav-faq-answer" [class.visible]="expandedFaqIndex() === $index">
                  <div class="sav-faq-answer-body" [innerHTML]="faq.answer | trustedCmsHtml"></div>
                </div>
              </div>
            }
          </div>
        </div>
      </section>

      @if (longDescriptionHtml()) {
        <app-longform-content [html]="longDescriptionHtml()" />
      }

      @if (hasFeaturedBlogs()) {
        <app-latest-insights [blogs]="featuredBlogs()" />
      }

      @if (hasReviews()) {
        <section class="sav-reviews" aria-label="Shopper reviews">
          <header class="sav-reviews-head">
            <div class="sav-reviews-title-wrap">
              <div class="sav-reviews-eyebrow">
                <span aria-hidden="true"></span>
                <p>Social Proof</p>
              </div>
              <h2>What shoppers say.</h2>
              <p>Ratings and written reviews from shoppers who used these offers.</p>
            </div>

            <div class="sav-reviews-controls">
              <div class="sav-reviews-score">
                <div class="sav-score-stars" aria-hidden="true">
                  @for (_star of [1, 2, 3, 4, 5]; track _star) {
                    <svg
                      width="18"
                      height="17"
                      viewBox="0 0 18 17"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 17.27L16.15 19.78C16.91 20.24 17.84 19.56 17.64 18.7L16.54 13.98 20.21 10.8C20.88 10.22 20.52 9.12 19.64 9.05L14.81 8.64 12.92 4.18C12.58 3.37 11.42 3.37 11.08 4.18L9.19 8.63 4.36 9.04C3.48 9.11 3.12 10.21 3.79 10.79L7.46 13.97 6.36 18.69C6.16 19.55 7.09 20.23 7.85 19.77L12 17.27Z"
                        fill="currentColor"
                        transform="translate(-3 -3)"
                      ></path>
                    </svg>
                  }
                </div>
                <span aria-hidden="true"></span>
                <h3>
                  {{ reviewsRating() }} / 5.0 <small>({{ reviewsCount() }} Reviews)</small>
                </h3>
              </div>
            </div>
          </header>

          <div class="sav-reviews-track">
            @for (review of reviews(); track review.id) {
              <article class="sav-review-card">
                <div class="sav-review-card-top">
                  <div class="sav-review-stars" aria-hidden="true">
                    @for (_star of review.stars; track _star) {
                      <svg
                        width="18"
                        height="17"
                        viewBox="0 0 18 17"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 17.27L16.15 19.78C16.91 20.24 17.84 19.56 17.64 18.7L16.54 13.98 20.21 10.8C20.88 10.22 20.52 9.12 19.64 9.05L14.81 8.64 12.92 4.18C12.58 3.37 11.42 3.37 11.08 4.18L9.19 8.63 4.36 9.04C3.48 9.11 3.12 10.21 3.79 10.79L7.46 13.97 6.36 18.69C6.16 19.55 7.09 20.23 7.85 19.77L12 17.27Z"
                          fill="currentColor"
                          transform="translate(-3 -3)"
                        ></path>
                      </svg>
                    }
                  </div>
                  <h4 class="type-card-title">{{ review.title }}</h4>
                </div>
                <p>{{ review.description }}</p>
                <div class="sav-review-card-foot">
                  <div class="sav-review-card-user">
                    <span aria-hidden="true">{{ review.initials }}</span>
                    <div>
                      <strong>{{ review.userName }}</strong>
                      @if (review.roleLabel) {
                        <small>{{ review.roleLabel }}</small>
                      }
                    </div>
                  </div>
                  @if (review.dateLabel) {
                    <p>{{ review.dateLabel }}</p>
                  }
                </div>
              </article>
            }
          </div>

          <div class="sav-reviews-cta">
            <button type="button">Write a Review</button>
          </div>
        </section>
      }
    } @placeholder {
      <section class="sav-category-deferred-placeholder" aria-hidden="true"></section>
    }
  </div>
</section>
